import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FlashOnIcon from "@material-ui/icons/FlashOn";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const truncate = (text, max = 80) =>
  text && text.length > max ? `${text.slice(0, max)}…` : text || "—";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newItems = [];
    if (Array.isArray(quickmessages)) {
      quickmessages.forEach((qm) => {
        const idx = state.findIndex((u) => u.id === qm.id);
        if (idx !== -1) state[idx] = qm;
        else newItems.push(qm);
      });
    }
    return [...state, ...newItems];
  }
  if (action.type === "UPDATE_QUICKMESSAGES") {
    const qm = action.payload;
    const idx = state.findIndex((u) => u.id === qm.id);
    if (idx !== -1) { state[idx] = qm; return [...state]; }
    return [qm, ...state];
  }
  if (action.type === "DELETE_QUICKMESSAGE") {
    const id = action.payload;
    const idx = state.findIndex((u) => u.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  shortcodeText: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
  messageText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  mediaText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const QuickMessages = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickMessage, setSelectedQuickMessage] = useState(null);
  const [deletingQuickMessage, setDeletingQuickMessage] = useState(null);
  const [quickMessageModalOpen, setQuickMessageModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickMessages, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickMessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketConnection({ companyId, userId: user.id });

    socket.on(`company${companyId}-quickemessage`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    });
    return () => socket.disconnect();
  }, []);

  const fetchQuickMessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenModal = () => {
    setSelectedQuickMessage(null);
    setQuickMessageModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedQuickMessage(null);
    setQuickMessageModalOpen(false);
    fetchQuickMessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEdit = (qm) => {
    setSelectedQuickMessage(qm);
    setQuickMessageModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/quick-messages/${id}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickMessage(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    fetchQuickMessages();
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickMessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickMessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingQuickMessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickMessages();
        }}
        open={quickMessageModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickMessage && selectedQuickMessage.id}
      />

      <MainHeader>
        <Title>{i18n.t("quickMessages.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("quickMessages.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
          >
            {i18n.t("quickMessages.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">{i18n.t("quickMessages.table.shortcode")}</TableCell>
              <TableCell align="left">{i18n.t("quickMessages.dialog.message")}</TableCell>
              <TableCell align="left">{i18n.t("quickMessages.table.mediaName")}</TableCell>
              <TableCell align="center">{i18n.t("quickMessages.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {!loading && quickMessages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box className={classes.emptyState}>
                      <FlashOnIcon className={classes.emptyIcon} />
                      <Typography variant="body2" gutterBottom>
                        {searchParam
                          ? `Nenhum atalho encontrado para "${searchParam}"`
                          : "Nenhuma resposta rápida cadastrada ainda."}
                      </Typography>
                      {!searchParam && (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={handleOpenModal}
                          style={{ marginTop: 8 }}
                        >
                          Adicionar primeira resposta
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {quickMessages.map((qm) => (
                <TableRow key={qm.id} hover>
                  <TableCell align="left">
                    <Typography className={classes.shortcodeText}>
                      /{qm.shortcode}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip title={qm.message || ""} placement="top-start">
                      <Typography className={classes.messageText}>
                        {truncate(qm.message)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    <Typography className={classes.mediaText}>
                      {qm.mediaName || i18n.t("quickMessages.noAttachment")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(qm)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingQuickMessage(qm);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default QuickMessages;
